<template>
    <div class="step-federal">
        <ConfirmModal />
        <h3>3. Import PDF fédéral (FFD)</h3>
        <p class="hint">
            Pas d'aperçu pour cette source -- le PDF donne déjà les points finaux par catégorie, un fichier peut
            couvrir plusieurs tournois nationaux à la fois.
        </p>

        <div class="form-group">
            <label>Fichier PDF FFD</label>
            <input type="file" accept=".pdf" @change="onFile" />
        </div>

        <div v-if="result" class="ok-banner">
            ✅ Import "{{ result.category }}" saison {{ result.season }} -- {{ result.entries_count }} entrée(s).
        </div>

        <div class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('back')">← Retour</button>
            <button type="button" class="btn-submit" :disabled="!file || uploading" @click="onUpload">
                {{ uploading ? 'Envoi...' : 'Importer' }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { toast } from 'vue3-toastify'
import dartsHubApi from '@/axios/dartsHubApi'
import { handleError } from '@/functions/utils'
import ConfirmModal from '@/components/ConfirmModal.vue'
import { useConfirm } from '@/composables/useConfirm'

const emit = defineEmits<{
    next: [result: { category: string; season: string; entries_count: number; ranking_tournament_ids: number[] }]
    back: []
}>()

const { confirm } = useConfirm()

const file = ref<File | null>(null)
const uploading = ref(false)
const result = ref<{ category: string; season: string; entries_count: number } | null>(null)

function onFile(event: Event) {
    file.value = (event.target as HTMLInputElement).files?.[0] ?? null
}

async function onUpload() {
    if (!file.value) return
    const accepted = await confirm(`Importer "${file.value.name}" comme classement fédéral officiel ?`)
    if (!accepted) return

    uploading.value = true
    try {
        const formData = new FormData()
        formData.append('file', file.value)
        const { data } = await dartsHubApi.post('/leaderboard/federal/import-pdf', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        result.value = data
        toast.success('PDF fédéral importé.')
        emit('next', data)
    } catch (err) {
        handleError(err, 'import du PDF fédéral')
    } finally {
        uploading.value = false
    }
}
</script>

<style scoped>
.step-federal h3 {
    margin-bottom: 1rem;
}

.form-group {
    margin: 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.form-group label {
    font-weight: 600;
    font-size: 0.9rem;
}

.hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
}

.ok-banner {
    color: var(--color-success, #38c793);
    font-weight: 600;
    margin: 1rem 0;
}

.form-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
}

.btn-submit,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.2rem;
    border-radius: var(--radius, 8px);
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--color-accent, #1a3a5c);
}

.btn-submit {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
}

.btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    color: var(--color-accent, #1a3a5c);
}
</style>
